import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import ProgressBar from "@/components/ProgressBar";
import QuestionCard from "@/components/QuestionCard";
import { ArrowRight, ArrowLeft, Loader2, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";

export interface AssessmentData {
  email: string;
  name: string;
  appType: string;
  experienceIntent: string;
  usageFrequency: string;
  sessionDuration: number;
  userMotivations: string[];
  mainChallenge: string;
  sensoryPreferences: string[];
}

const Assessment = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [tokenValidated, setTokenValidated] = useState(false);
  const totalSteps = 8;

  const [assessmentData, setAssessmentData] = useState<AssessmentData>({
    email: "",
    name: "",
    appType: "",
    experienceIntent: "",
    usageFrequency: "",
    sessionDuration: 15,
    userMotivations: [],
    mainChallenge: "",
    sensoryPreferences: [],
  });

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      const token = searchParams.get('token');
      
      console.log('[Assessment] Token from URL:', token);
      
      // Check if user is logged in admin
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      console.log('[Assessment] Session check:', { 
        hasSession: !!session, 
        userId: session?.user?.id,
        email: session?.user?.email,
        sessionError 
      });
      
      if (session) {
        // Check if user is admin
        const { data: roleData, error: roleError } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', session.user.id)
          .eq('role', 'admin')
          .maybeSingle();
        
        console.log('[Assessment] Admin role check:', { 
          roleData, 
          roleError,
          isAdmin: !!roleData 
        });
        
        if (roleData) {
          // Admin can access without token
          console.log('[Assessment] Admin access granted - bypassing token validation');
          setTokenValidated(true);
          setAssessmentData(prev => ({
            ...prev,
            email: session.user.email || '',
          }));
          setIsValidating(false);
          return;
        } else {
          console.log('[Assessment] User is not admin or role check failed');
        }
      }
      
      if (!token) {
        setValidationError('No access token provided. Please use the link from your payment confirmation email or visit the admin panel to generate a test token.');
        setIsValidating(false);
        return;
      }

      setIsValidating(true);
      setValidationError(null);

      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const response = await fetch(
          `${supabaseUrl}/functions/v1/validate-token?token=${encodeURIComponent(token)}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        const data = await response.json();

        if (data.valid) {
          // Pre-fill email and name if available
          setAssessmentData(prev => ({
            ...prev,
            email: data.email || '',
            name: data.name || '',
          }));
          setTokenValidated(true);
        } else {
          setValidationError(data.error || 'Invalid access token');
        }
      } catch (error) {
        console.error('Token validation error:', error);
        setValidationError('Failed to validate access token. Please try again or contact support.');
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [searchParams]);

  const validateCurrentStep = (): boolean => {
    switch (currentStep) {
      case 1:
        if (!assessmentData.email) {
          toast({ title: "Email required", variant: "destructive" });
          return false;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(assessmentData.email)) {
          toast({ title: "Valid email required", variant: "destructive" });
          return false;
        }
        return true;
      case 2:
        return !!assessmentData.appType;
      case 3:
        return !!assessmentData.experienceIntent;
      case 4:
        return !!assessmentData.usageFrequency;
      case 5:
        return assessmentData.sessionDuration >= 1 && assessmentData.sessionDuration <= 120;
      case 6:
        if (assessmentData.userMotivations.length === 0) {
          toast({ title: "Select at least one motivation", variant: "destructive" });
          return false;
        }
        if (assessmentData.userMotivations.length > 3) {
          toast({ title: "Select maximum 3 motivations", variant: "destructive" });
          return false;
        }
        return true;
      case 7:
        return !!assessmentData.mainChallenge;
      case 8:
        if (assessmentData.sensoryPreferences.length === 0) {
          toast({ title: "Select at least one sensory preference", variant: "destructive" });
          return false;
        }
        if (assessmentData.sensoryPreferences.length > 8) {
          toast({ title: "Select maximum 8 sensory preferences", variant: "destructive" });
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (!validateCurrentStep()) return;

    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const token = searchParams.get('token');
      
      // Check if user is admin (can submit without token)
      const { data: { session } } = await supabase.auth.getSession();
      let isAdminUser = false;
      
      if (session) {
        const { data: roleData } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', session.user.id)
          .eq('role', 'admin')
          .maybeSingle();
        
        isAdminUser = !!roleData;
      }
      
      if (!token && !isAdminUser) {
        throw new Error('Access token missing');
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      
      // Build headers - include Authorization for admin users
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      // For admin users, let server generate the temp token
      // Client just sends empty token and Authorization header
      let submissionToken = token;
      if (isAdminUser && session && !token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
        // Generate a simple admin token - server will validate via Authorization header
        const uuid = crypto.randomUUID();
        const timestamp = Date.now();
        submissionToken = `ADMIN_${uuid}_${timestamp}`;
      }
      
      // Map frontend field names to backend field names
      const payload = {
        token: submissionToken,
        email: assessmentData.email,
        name: assessmentData.name,
        app_type: assessmentData.appType,
        experience_intent: assessmentData.experienceIntent,
        usage_frequency: assessmentData.usageFrequency,
        session_duration: assessmentData.sessionDuration,
        user_motivations: assessmentData.userMotivations,
        main_challenge: assessmentData.mainChallenge,
        sensory_preferences: assessmentData.sensoryPreferences,
      };

      const response = await fetch(
        `${supabaseUrl}/functions/v1/submit-assessment`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to submit assessment');
      }

      // Show success message
      toast({
        title: "Success! 🎉",
        description: isAdminUser 
          ? "Your test strategy has been generated!" 
          : "Your strategy is being generated and will be emailed to you shortly.",
      });

      // Navigate to results page
      navigate("/results", { 
        state: { 
          assessmentData, 
          submitted: true,
          recommendedTier: data.recommended_tier,
          strategy: data.strategy,
          strategyPdfUrl: data.strategy_pdf_url,
          strategyJsonUrl: data.strategy_json_url,
        } 
      });
    } catch (error) {
      console.error("Error submitting assessment:", error);
      toast({
        title: "Submission Error",
        description: error instanceof Error ? error.message : "Failed to submit assessment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderQuestion = () => {
    switch (currentStep) {
      case 1:
        return (
          <QuestionCard
            title="Let's get started!"
            subtitle="Please provide your contact information"
          >
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={assessmentData.email}
                  onChange={(e) =>
                    setAssessmentData({ ...assessmentData, email: e.target.value })
                  }
                  disabled={tokenValidated && !!assessmentData.email}
                  required
                />
                {tokenValidated && assessmentData.email && (
                  <p className="text-xs text-muted-foreground">
                    Email verified from payment
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Name (Optional)</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Your name"
                  value={assessmentData.name}
                  onChange={(e) =>
                    setAssessmentData({ ...assessmentData, name: e.target.value })
                  }
                />
              </div>
            </div>
          </QuestionCard>
        );

      case 2:
        return (
          <QuestionCard
            title="What type of app are you building?"
            subtitle="Select the category that best describes your application"
          >
            <RadioGroup
              value={assessmentData.appType}
              onValueChange={(value) =>
                setAssessmentData({ ...assessmentData, appType: value })
              }
            >
              <div className="space-y-3">
                {[
                  "Education & Learning",
                  "Health & Wellness",
                  "Finance & Productivity",
                  "Entertainment & Gaming",
                  "Social & Community",
                  "E-commerce & Retail",
                  "Other",
                ].map((type) => (
                  <div key={type} className="flex items-center space-x-3 p-4 rounded-lg border border-border hover:bg-accent/5 transition-colors">
                    <RadioGroupItem value={type} id={type} />
                    <Label htmlFor={type} className="flex-1 cursor-pointer font-medium">
                      {type}
                    </Label>
                  </div>
                ))}
              </div>
            </RadioGroup>
          </QuestionCard>
        );

      case 3:
        return (
          <QuestionCard
            title="What experience intent do you want to create?"
            subtitle="Choose the emotional tone that aligns with your app's purpose"
          >
            <RadioGroup
              value={assessmentData.experienceIntent}
              onValueChange={(value) =>
                setAssessmentData({ ...assessmentData, experienceIntent: value })
              }
            >
              <div className="space-y-3">
                {[
                  { value: "Sacred & Reflective", desc: "Calm, mindful, contemplative atmosphere" },
                  { value: "Playful & Energetic", desc: "Lighthearted, fun, dynamic" },
                  { value: "Calm & Focused", desc: "Straightforward, goal-oriented, efficient" },
                  { value: "Social & Connected", desc: "Community-oriented, collaborative" },
                  { value: "Achievement & Progress", desc: "Goal-driven, milestone-focused" },
                  { value: "Creative & Expressive", desc: "Self-expression, personalization" },
                ].map((intent) => (
                  <div key={intent.value} className="flex items-start space-x-3 p-4 rounded-lg border border-border hover:bg-accent/5 transition-colors">
                    <RadioGroupItem value={intent.value} id={intent.value} className="mt-1" />
                    <div className="flex-1 cursor-pointer">
                      <Label htmlFor={intent.value} className="font-medium cursor-pointer">
                        {intent.value}
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">{intent.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </RadioGroup>
          </QuestionCard>
        );

      case 4:
        return (
          <QuestionCard
            title="How often should users engage with your app?"
            subtitle="Select the ideal usage frequency"
          >
            <RadioGroup
              value={assessmentData.usageFrequency}
              onValueChange={(value) =>
                setAssessmentData({ ...assessmentData, usageFrequency: value })
              }
            >
              <div className="space-y-3">
                {[
                  "Multiple times daily",
                  "Once daily",
                  "Few times per week",
                  "Weekly",
                  "Less frequently",
                ].map((frequency) => (
                  <div key={frequency} className="flex items-center space-x-3 p-4 rounded-lg border border-border hover:bg-accent/5 transition-colors">
                    <RadioGroupItem value={frequency} id={frequency} />
                    <Label htmlFor={frequency} className="flex-1 cursor-pointer font-medium">
                      {frequency}
                    </Label>
                  </div>
                ))}
              </div>
            </RadioGroup>
          </QuestionCard>
        );

      case 5:
        return (
          <QuestionCard
            title="How long will a typical user session last?"
            subtitle="Enter the duration in minutes (1-120)"
          >
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="duration">Session Duration (minutes)</Label>
                <Input
                  id="duration"
                  type="number"
                  min="1"
                  max="120"
                  value={assessmentData.sessionDuration}
                  onChange={(e) =>
                    setAssessmentData({
                      ...assessmentData,
                      sessionDuration: parseInt(e.target.value) || 15,
                    })
                  }
                  className="text-2xl font-bold text-center"
                />
                <p className="text-sm text-muted-foreground text-center">
                  Default: 15 minutes | Range: 1-120 minutes
                </p>
              </div>
            </div>
          </QuestionCard>
        );

      case 6:
        return (
          <QuestionCard
            title="What are your users' primary motivations?"
            subtitle="Select up to 3 motivations"
          >
            <div className="space-y-3">
              {[
                "Achievement & Progress",
                "Social Connection",
                "Self-Improvement",
                "Competition",
                "Exploration & Discovery",
                "Creativity & Expression",
                "Relaxation & Escape",
                "Learning & Mastery",
                "Recognition & Status",
                "Collaboration",
              ].map((motivation) => (
                <div key={motivation} className="flex items-center space-x-3 p-4 rounded-lg border border-border hover:bg-accent/5 transition-colors">
                  <Checkbox
                    id={motivation}
                    checked={assessmentData.userMotivations.includes(motivation)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        if (assessmentData.userMotivations.length < 3) {
                          setAssessmentData({
                            ...assessmentData,
                            userMotivations: [...assessmentData.userMotivations, motivation],
                          });
                        }
                      } else {
                        setAssessmentData({
                          ...assessmentData,
                          userMotivations: assessmentData.userMotivations.filter(
                            (m) => m !== motivation
                          ),
                        });
                      }
                    }}
                    disabled={
                      !assessmentData.userMotivations.includes(motivation) &&
                      assessmentData.userMotivations.length >= 3
                    }
                  />
                  <Label htmlFor={motivation} className="flex-1 cursor-pointer font-medium">
                    {motivation}
                  </Label>
                </div>
              ))}
            </div>
            <p className="text-sm text-muted-foreground mt-4 text-center">
              Selected: {assessmentData.userMotivations.length}/3
            </p>
          </QuestionCard>
        );

      case 7:
        return (
          <QuestionCard
            title="What's the main challenge preventing user engagement?"
            subtitle="Select the primary obstacle"
          >
            <RadioGroup
              value={assessmentData.mainChallenge}
              onValueChange={(value) =>
                setAssessmentData({ ...assessmentData, mainChallenge: value })
              }
            >
              <div className="space-y-3">
                {[
                  "Lack of time / Busy schedules",
                  "Forgetting to use the app",
                  "Lack of immediate results",
                  "Complexity / Learning curve",
                  "Low motivation / Energy",
                  "Emotional resistance",
                ].map((challenge) => (
                  <div key={challenge} className="flex items-center space-x-3 p-4 rounded-lg border border-border hover:bg-accent/5 transition-colors">
                    <RadioGroupItem value={challenge} id={challenge} />
                    <Label htmlFor={challenge} className="flex-1 cursor-pointer font-medium">
                      {challenge}
                    </Label>
                  </div>
                ))}
              </div>
            </RadioGroup>
          </QuestionCard>
        );

      case 8:
        return (
          <QuestionCard
            title="Which sensory elements fit your app?"
            subtitle="Select all that apply"
          >
            <div className="space-y-3">
              {[
                { value: "Visual rewards (animations, colors)", desc: "Motion graphics, color changes, visual effects" },
                { value: "Audio feedback (sounds, music)", desc: "Sound effects, background music, chimes" },
                { value: "Haptic feedback (vibrations)", desc: "Vibrations and tactile responses" },
                { value: "Text-based feedback", desc: "Written messages, notifications, tips" },
                { value: "Progress bars & meters", desc: "Visual completion indicators" },
                { value: "Celebratory moments", desc: "Confetti, achievements, milestone celebrations" },
                { value: "Subtle & minimal", desc: "Clean, understated feedback" },
                { value: "Bold & dramatic", desc: "Strong, impactful sensory moments" },
              ].map((element) => (
                <div key={element.value} className="flex items-start space-x-3 p-4 rounded-lg border border-border hover:bg-accent/5 transition-colors">
                  <Checkbox
                    id={element.value}
                    checked={assessmentData.sensoryPreferences.includes(element.value)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setAssessmentData({
                          ...assessmentData,
                          sensoryPreferences: [...assessmentData.sensoryPreferences, element.value],
                        });
                      } else {
                        setAssessmentData({
                          ...assessmentData,
                          sensoryPreferences: assessmentData.sensoryPreferences.filter(
                            (e) => e !== element.value
                          ),
                        });
                      }
                    }}
                  />
                  <div className="flex-1 cursor-pointer">
                    <Label htmlFor={element.value} className="font-medium cursor-pointer">
                      {element.value}
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">{element.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </QuestionCard>
        );

      default:
        return null;
    }
  };

  // Show loading state while validating token
  if (isValidating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 py-12 px-4 flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary" />
          <h2 className="text-2xl font-bold">Validating your access...</h2>
          <p className="text-muted-foreground">Please wait while we verify your token.</p>
        </div>
      </div>
    );
  }

  // Show error if token validation failed
  if (validationError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 py-12 px-4 flex items-center justify-center">
        <div className="max-w-2xl w-full mx-auto">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Access Denied</AlertTitle>
            <AlertDescription className="mt-2">
              {validationError}
            </AlertDescription>
          </Alert>
          <div className="mt-6 text-center space-y-4">
            <p className="text-muted-foreground">
              💡 <strong>Options:</strong>
            </p>
            <div className="flex flex-col gap-3">
              <Button asChild>
                <Link to="/admin">Generate Test Token (Admin)</Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/pricing">Purchase Access</Link>
              </Button>
              <Button variant="ghost" asChild>
                <Link to="/">Go Home</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold mb-2">Let's Create Your Strategy! 🎯</h1>
          <p className="text-muted-foreground">
            Answer these questions about your app and users. This takes about 10 minutes.
          </p>
          <div className="mt-4 p-4 bg-primary/5 rounded-lg border border-primary/20 text-sm text-muted-foreground space-y-1">
            <p>📝 All questions are required</p>
            <p>⏱️ You can take your time</p>
            <p>📧 Your strategy will be emailed when complete</p>
          </div>
        </div>

        <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />
        
        {renderQuestion()}

        <div className="flex justify-between mt-8 max-w-2xl mx-auto">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1 || isSubmitting}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button
            onClick={handleNext}
            disabled={isSubmitting}
            className="bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : currentStep === totalSteps ? (
              "Submit Assessment"
            ) : (
              <>
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Assessment;
